import React from 'react';
import BooleanPageCondition from './boolean-page-condition';
import ScalarPageCondition from './scalar-page-condition';
import PageActions from '../../actions/page';
import SharedSource from '../../sources/shared';
import GenericSelectBox from '../generic/GenericSelectBox';
import { Alert, Button } from 'react-bootstrap';
import { merge } from 'ramda';
import {
    generateDummyBoolPageCondition, generateDummyScalarPageCondition,
} from '../../utils/page-conditions';
import translations from '../../translations';
import '../../../css/flow/config.less';

const VIEW = {
    SELECT: 0,
    BOOLEAN: 1,
    SCALAR: 2,
};

/**
 * @param {Object} page Represents the page currently in page editor model
 * @param {String} tenantId
 * @param {String} authenticationToken
 * @description A container component that displays tools for creating page conditions
 */
class PageConditionsWrapper extends React.Component {

    constructor() {
        super();
        this.state = {
            alert: null,
            isLoading: false,
            metadata: null,
            view: VIEW.SELECT,
            component: null,
        };
    }

    componentDidMount() {
        this.fetchPageData();
    }

    openComplexPageConditions = (e) => {
        e.preventDefault();
        PageActions.setEditingType(
            'CONDITIONS',
            this.props.page.id.value,
        );
    }

    onCancel = () => {
        PageActions.setEditingType(
            null,
            this.props.page.id.value,
        );
    }

    componentHasValue = (componentId) => {
        const component = this.props.page.pageComponents.data.find(component => component.id.value === componentId);
        const valueProps = component.valueElementValueBindingReferenceId.data;
        if (valueProps !== null) {
            return true;
        }

        return false;
    }

    checkComponentValueType = (e) => {
        const componentId = e.target.value;
        const component = this.props.page.pageComponents.data.find(component => component.id.value === componentId);
        const selectedComponent = {
            id: component.id.value,
            name: component.developerName.value,
        };
        const valueProps = component.valueElementValueBindingReferenceId.data;
        if (valueProps !== null) {
            const assocValue = valueProps[0].properties.find(prop => prop.developerName === 'ContentType');

            if (assocValue.contentValue === 'ContentBoolean') {
                this.setState({
                    view: VIEW.BOOLEAN,
                    component: selectedComponent,
                });
            } else {
                this.setState({
                    view: VIEW.SCALAR,
                    component: selectedComponent,
                });
            }

        } else {
            this.setState({
                view: VIEW.SELECT,
                component: selectedComponent,
            });
        }
    }

    savePageCondition = (request, valueComparison = null) => {
        SharedSource.save(request, 'page', this.props.tenantId, this.props.authenticationToken)
            .then((response) => {

                // This is so that the newly created page condition
                // appears in the list when invoking the page condition
                // system flow. We are basically updating the clint side model
                // that describes the number of page conditions.
                // TODO: This is a temporary workaround until the entire
                // page conditions toolset has been refactored into React components
                let objectData = this.props.page.pageConditions.data;
                if (objectData === null) {

                    // If no page conditions then property value is null..we need an empty array
                    objectData = merge(this.props.page.pageConditions, { data: [] }).data;
                }
                if (response.pageConditions.length === request.pageConditions.length) {
                    if (this.state.view === VIEW.BOOLEAN) {
                        objectData.push(
                            generateDummyBoolPageCondition(
                                request.pageConditions[0],
                                this.props.page,
                            ),
                        );
                    } else {
                        objectData.push(
                            generateDummyScalarPageCondition(
                                request.pageConditions[0],
                                this.props.page,
                                valueComparison,
                            ),
                        );
                    }
                    PageActions.setPageConditions(objectData, this.props.page.id.value);
                    this.fetchPageData(
                        {
                            status: 'success',
                            message: translations.PAGE_COND_BASIC_create_success,
                        },
                    );
                }
            })
            .catch((response) => {
                this.fetchPageData(
                    {
                        status: 'danger',
                        message: response.message,
                    },
                );
            });
    }

    fetchPageData = (alert = null) => {
        this.setState({ isLoading: true });
        SharedSource.get(this.props.page.id.value, 'page', this.props.tenantId, this.props.authenticationToken)
            .then((response) => {
                this.setState({
                    alert,
                    metadata: JSON.parse(response),
                    isLoading: false,
                });
            })
            .catch((response) => {
            // The page must be saved before the
            // boolean page condition editor can be used
                const notYetSaved = 'The requested Element does not exist';
                let errorMsg = response.message;
                if (response.message.indexOf(notYetSaved) > -1) {
                    errorMsg = translations.PAGE_COND_BASIC_error_unsaved_page;
                }
                this.setState({ alert: {
                    status: 'danger',
                    message: errorMsg,
                },
                isLoading: false,
                });
            });
    }

    showValidationError = () => {
        this.setState({
            alert: {
                status: 'danger',
                message: translations.PAGE_COND_BASIC_error_validation,
            },
        });
    };

    renderCancel = () => {
        if (this.state.view === VIEW.SELECT) {
            return (
                <div className="page-config-footer">
                    <Button bsStyle="default" className="btn-outline" onClick={this.onCancel}>Cancel</Button>
                    <Button bsStyle="primary" disabled>{ translations.PAGE_COND_BASIC_save }</Button>
                </div>
            );
        }
        return null;

    }

    render() {
        let tool = null;
        let alert = null;
        let warning = null;
        const pageComponents = [];

        if (this.state.alert !== null) {
            alert = (
                <Alert bsStyle={this.state.alert.status}>
                    <p><strong>{this.state.alert.message}</strong></p>
                </Alert>
            );
            if (this.state.alert.status === 'success') {
                setTimeout(() => {
                    this.onCancel();
                }, 1000);
            }
        }

        if (this.props.page.pageComponents.data.length <= 1) {
            warning = (
                <Alert bsStyle="warning">
                    <p><strong>{ translations.PAGE_COND_BASIC_error_no_components }</strong></p>
                </Alert>
            );
        }

        this.props.page.pageComponents.data.forEach((component) => {
            if (this.componentHasValue(component.id.value)) {
                pageComponents.push(<option key={component.developerName.value} value={component.id.value}>{component.developerName.value}</option>);
            }
        });

        pageComponents.unshift(
            <option key="default" value="" disabled hidden>-- Please Select --</option>,
        );

        const initialSelectBoxes = (
            <>
                <h1>{ translations.PAGE_COND_BASIC_title }</h1>
                <p className="margin-bottom-large">
                    {
                        translations.PAGE_COND_BASIC_instructions_1
                    }
                </p>
                <p className="margin-bottom">
                    {
                        translations.PAGE_COND_BASIC_instructions_2
                    }
                    {' '}
                    <button className="advanced link-emulate" onClick={this.openComplexPageConditions}>
                        {
                            translations.PAGE_COND_BASIC_instructions_3
                        }
                    </button>
                </p>
                {alert}
                {warning}
                <div className="form-inline float-left">
                    <span className="align-label">{ translations.PAGE_COND_BASIC_value_of }</span>
                    <GenericSelectBox
                        key="selectComponent"
                        required={false}
                        onFilter={this.checkComponentValueType}
                        selected={this.state.component ? this.state.component.id : ''}
                    >
                        {pageComponents}
                    </GenericSelectBox>
                </div>
            </>
        );

        if (this.state.metadata !== null) {
            if (this.state.view === VIEW.BOOLEAN) {
                tool = (
                    <BooleanPageCondition
                        initialComponent={initialSelectBoxes}
                        components={this.props.page.pageComponents.data}
                        metaData={this.state.metadata}
                        onApply={this.savePageCondition}
                        selectedComponent={this.state.component}
                        onCancel={this.onCancel}
                        showValidationError={this.showValidationError}
                    />
                );
            }

            if (this.state.view === VIEW.SCALAR) {
                tool = (
                    <ScalarPageCondition
                        initialComponent={initialSelectBoxes}
                        components={this.props.page.pageComponents.data}
                        metaData={this.state.metadata}
                        onApply={this.savePageCondition}
                        selectedComponent={this.state.component}
                        onCancel={this.onCancel}
                        showValidationError={this.showValidationError}
                    />
                );
            }
        }


        return (
            <div className="page-config page-conditions-basic" key="page-config">
                {tool || (
                    // If there is no tool (basic or scalar page conditions editor)
                    // then just display the initial set of instructions and select boxes
                    <>
                        <div className="overflow-y-auto">
                            {initialSelectBoxes}
                        </div>
                        {this.renderCancel()}
                    </>
                )}
                {React.createElement(manywho.component.getByName('wait'), { isVisible: this.state.isLoading }, null)}
            </div>
        );
    }
}

export default PageConditionsWrapper;
